import { type BrandColors, deriveBrandTheme, toBrandColors } from "@/config/themes/brandColors";
import { darkThemeConfig } from "@/config/themes/dark";
import { lightThemeConfig } from "@/config/themes/light";
import type {
  AppearancePreference,
  TenantThemeConfig,
  ThemeMode,
} from "@/config/themes/types";
import {
  APPEARANCE_STORAGE_KEY,
  BRAND_COLORS_STORAGE_KEY,
  THEME_MODE,
} from "@/constants";

const themeByMode: Record<ThemeMode, TenantThemeConfig> = {
  dark: darkThemeConfig,
  light: lightThemeConfig,
};

function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === THEME_MODE.LIGHT || value === THEME_MODE.DARK;
}

function getSystemThemeMode(): ThemeMode {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return THEME_MODE.DARK;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? THEME_MODE.DARK : THEME_MODE.LIGHT;
}

export function readStoredAppearancePreference(): AppearancePreference {
  try {
    const stored = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (isAppearancePreference(stored)) {
      return stored;
    }
    const detected = getSystemThemeMode();
    localStorage.setItem(APPEARANCE_STORAGE_KEY, detected);
    return detected;
  } catch {
    // localStorage may be unavailable in restricted contexts.
  }
  return getSystemThemeMode();
}

/**
 * The theme for `mode`: Olive's hand-tuned one, or — when the tenant has brand colours — that one
 * with its brand-carrying colours derived from them.
 */
export function getThemeConfig(mode: ThemeMode, brand?: BrandColors | null): TenantThemeConfig {
  const base = themeByMode[mode];
  return brand ? deriveBrandTheme(base, brand) : base;
}

/** The brand colours last seen for this tenant, or `null` if none were set or storage is blocked. */
export function readStoredBrandColors(): BrandColors | null {
  try {
    const stored = window.localStorage.getItem(BRAND_COLORS_STORAGE_KEY);
    if (stored === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const { primary, secondary } = parsed as Record<string, unknown>;
    return toBrandColors(
      typeof primary === "string" ? primary : null,
      typeof secondary === "string" ? secondary : null,
    );
  } catch {
    // Unavailable or corrupt storage just means painting Olive's theme until the request returns.
    return null;
  }
}

/** Remember (or, with `null`, forget) the tenant's brand colours for the next load. */
export function storeBrandColors(brand: BrandColors | null): void {
  try {
    if (brand === null) {
      window.localStorage.removeItem(BRAND_COLORS_STORAGE_KEY);
    } else {
      window.localStorage.setItem(BRAND_COLORS_STORAGE_KEY, JSON.stringify(brand));
    }
  } catch {
    // localStorage may be unavailable in restricted contexts.
  }
}

type CssVariableMapping = readonly [property: `--${string}`, value: string];

function themeToCssVariableMappings(theme: TenantThemeConfig): CssVariableMapping[] {
  const { colors, overlay, typography } = theme;

  return [
    ["--theme-primary", colors.primary],
    ["--theme-primary-dark", colors.primaryDark],
    ["--theme-primary-light", colors.primaryLight],
    ["--theme-secondary", colors.secondary],
    ["--theme-accent", colors.accent],
    ["--theme-error", colors.error],
    ["--theme-success", colors.success],
    ["--theme-warning", colors.warning],
    ["--theme-info", colors.info],
    ["--theme-severity-high", colors.severityHigh],
    ["--theme-severity-medium", colors.severityMedium],
    ["--theme-severity-low", colors.severityLow],
    ["--theme-background", colors.background],
    ["--theme-surface", colors.surface],
    ["--theme-surface-alt", colors.surfaceAlt],
    ["--theme-surface-raised", colors.surfaceRaised],
    ["--theme-border", colors.border],
    ["--theme-sidebar-border", colors.sidebarBorder],
    ["--theme-text-primary", colors.textPrimary],
    ["--theme-text-secondary", colors.textSecondary],
    ["--theme-text-disabled", colors.textDisabled],
    ["--theme-text-on-primary", colors.textOnPrimary],
    ["--theme-gradientBackground", colors.gradientBackground],
    ["--theme-hover-primary", colors.hoverPrimary],
    ["--theme-hover-secondary", colors.hoverSecondary],
    ["--theme-hover-tertiary", colors.hoverTertiary],
    ["--theme-surface-hover", colors.surfaceHover],
    ["--theme-overlay-light-05", overlay.light05],
    ["--theme-overlay-light-10", overlay.light10],
    ["--theme-overlay-light-20", overlay.light20],
    ["--theme-overlay-olive-15", overlay.olive15],
    ["--theme-font-family", typography.fontFamily],
  ];
}

function themeToCssVariables(theme: TenantThemeConfig): Record<string, string> {
  return Object.fromEntries(themeToCssVariableMappings(theme));
}

export function applyCssVariables(theme: TenantThemeConfig): void {
  const root = document.documentElement;
  const variables = themeToCssVariables(theme);

  for (const [name, value] of Object.entries(variables)) {
    root.style.setProperty(name, value);
  }

  root.dataset.appearance = theme.mode;
  root.style.colorScheme = theme.mode;
}
